import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { getDynamicAssetTireInfo, getOrderedTireCodes } from '../util';
import type { Asset } from 'types/asset.types';
import { Selector } from 'components/Form/Selectors/Selector';
import { type SensorType, SENSOR_TYPE_OPTIONS } from 'config/tpmsSensors';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const rolesAllowedToEditSensor = [
  'director',
  'external-admin',
  'external-superadmin',
  'external-director',
];

const EditableSensorTable = ({
  enableDarkTheme,
  assetTires,
  selectedAsset,
  updateSensor,
  role,
  makeModelDetails,
  tirePatterns,
  customerId,
}: {
  enableDarkTheme: boolean;
  assetTires: any[];
  selectedAsset: Asset;
  updateSensor: any;
  role: string | null;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
}) => {
  const { t } = useTranslation();
  const initialData = useMemo(() => {
    const assetTireDetails = getDynamicAssetTireInfo(
      makeModelDetails,
      tirePatterns.tirePatternPositions,
      selectedAsset.make,
      selectedAsset.model,
      customerId
    );
    const dataKeys = assetTires.map((item: any) => item.tireCode);
    let orderedCodes = getOrderedTireCodes(
      dataKeys,
      assetTireDetails.patternName
    );

    // Apply custom ordering for 6x4 vehicles specifically for this table
    if (assetTireDetails.patternName === '6x4') {
      const customOrder = ['0x17', '0x19', '0x26', '0x27', '0x29', '0x2A'];
      orderedCodes = customOrder.filter((code) => dataKeys.includes(code));
    }

    return orderedCodes
      .map((code) => {
        const item = assetTires.find((t: any) => t.tireCode === code);
        if (!item) return null;

        const rawId = item.sensorId;
        const type: SensorType | '' = item.sensorType || '';
        const sensorInput = item.sensorInput || '';

        return {
          id: item.tireCode,
          tire: assetTireDetails.tireCodes[code],
          sensorInput: sensorInput,
          rawSensorId: rawId,
          sensorType: type,
        };
      })
      .filter(Boolean) as any[];
  }, [assetTires, makeModelDetails, tirePatterns, selectedAsset, customerId]);

  const [rowData, setRowData] = useState(initialData);
  const [originalData] = useState(JSON.parse(JSON.stringify(initialData)));
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const palette =
    clientThemePalette[CLIENT_THEME][enableDarkTheme ? 'dark' : 'light'];
  const modalTitleBgColor = enableDarkTheme
    ? palette.toolBarBackground
    : palette.topBar;
  const modalTextColor = palette.textPrimary;
  const modalBorderColor = palette.borderGrey || palette.border;
  const dialogBgColor = palette.boxBackground;
  const accentColor = palette.primary;

  const canEditSensor = role ? rolesAllowedToEditSensor.includes(role) : false;

  console.log(role, canEditSensor);
  const handleSensorIdChange = (id: string, newSensorId: string) => {
    if (!canEditSensor) return;

    const cleanId = newSensorId.trim();
    let detectedType: SensorType | null = null;

    if (cleanId.length === 4) {
      detectedType = 'SST';
    } else if (cleanId.length === 6) {
      detectedType = 'DG';
    } else if (cleanId.length === 8) {
      detectedType = 'Tirecheck';
    }

    setRowData((prevData: any) =>
      prevData.map((row: any) =>
        row.id === id
          ? {
              ...row,
              sensorInput: newSensorId,
              sensorType: detectedType ?? row.sensorType,
            }
          : row
      )
    );
  };

  const handleSensorTypeChange = (id: string, newType: SensorType | '') => {
    if (!canEditSensor) return;

    setRowData((prevData: any) =>
      prevData.map((row: any) => {
        if (row.id === id) {
          return {
            ...row,
            sensorType: newType,
          };
        }
        return row;
      })
    );
  };

  const handleResetChanges = () => {
    if (!canEditSensor) return;
    setRowData(JSON.parse(JSON.stringify(originalData)));
  };

  const handleSaveClick = () => {
    if (!canEditSensor) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = () => {
    const payload = rowData.map((row: any) => ({
      ...row,
      sensorInput: row.sensorInput,
      sensorType: row.sensorType || null,
    }));
    void updateSensor(payload);
    setShowConfirmDialog(false);
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);
  };

  const hasChanges = JSON.stringify(rowData) !== JSON.stringify(originalData);
  const canSaveChanges =
    canEditSensor &&
    hasChanges &&
    new Set(
      rowData
        .map((t) =>
          typeof t.sensorInput === 'string' ? t.sensorInput.trim() : ''
        )
        .filter((id) => id !== '')
    ).size ===
      rowData.filter(
        (t) => typeof t.sensorInput === 'string' && t.sensorInput.trim() !== ''
      ).length;

  const changes = rowData
    .map((row, index) => {
      const original = originalData[index];
      if (row.sensorInput !== original.sensorInput) {
        return {
          tire: row.tire,
          tireId: row.id,
          from: original.sensorInput || '(empty)',
          to: row.sensorInput || '(empty)',
        };
      }
      return null;
    })
    .filter(Boolean);

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          height: 'calc(100vh - 330px)',
          maxHeight: '400px',
          backgroundColor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  backgroundColor: modalTitleBgColor,
                  color: modalTextColor,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: `1px solid ${modalBorderColor}`,
                  width: '10%',
                }}
              >
                {t('location_number')}
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: modalTitleBgColor,
                  color: modalTextColor,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: `1px solid ${modalBorderColor}`,
                  width: '30%',
                }}
              >
                {t('tire_position')}
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: modalTitleBgColor,
                  color: modalTextColor,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: `1px solid ${modalBorderColor}`,
                  width: '30%',
                }}
              >
                {t('sensor_type')}
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: modalTitleBgColor,
                  color: modalTextColor,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  borderBottom: `1px solid ${modalBorderColor}`,
                  width: '30%',
                }}
              >
                {t('sensor_id')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowData.map((row, index) => (
              <TableRow
                key={row.id}
                sx={{
                  '&:nth-of-type(odd)': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255,255,255,0.02)'
                      : 'rgba(0,0,0,0.02)',
                  },
                  '&:hover': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.04)',
                  },
                  height: '45px',
                }}
              >
                <TableCell
                  sx={{
                    color: modalTextColor,
                    borderBottom: `1px solid ${modalBorderColor}`,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                  }}
                >
                  {index + 1}
                </TableCell>
                <TableCell
                  sx={{
                    color: modalTextColor,
                    borderBottom: `1px solid ${modalBorderColor}`,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                  }}
                >
                  {row.tire}
                </TableCell>
                <TableCell
                  sx={{
                    color: modalTextColor,
                    borderBottom: `1px solid ${modalBorderColor}`,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                  }}
                >
                  {canEditSensor ? (
                    <Selector
                      minWidth="160px"
                      value={row.sensorType}
                      onChange={(event) => {
                        handleSensorTypeChange(
                          row.id,
                          event.target.value as SensorType
                        );
                      }}
                      selectorOptions={SENSOR_TYPE_OPTIONS as any}
                      enableDarkTheme={enableDarkTheme}
                      fallbackValue="Select Type"
                    />
                  ) : (
                    <Box
                      sx={{
                        color: modalTextColor,
                        fontSize: '0.875rem',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '14px',
                      }}
                    >
                      {row.sensorType}
                    </Box>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    color: modalTextColor,
                    borderBottom: `1px solid ${modalBorderColor}`,
                    fontSize: '0.875rem',
                    padding: '8px 16px',
                  }}
                >
                  {canEditSensor ? (
                    <TextField
                      size="small"
                      value={row.sensorInput ?? ''}
                      onChange={(e) => {
                        handleSensorIdChange(row.id, e.target.value);
                      }}
                      placeholder={t('enter_sensor_id')}
                      sx={{
                        minWidth: 160,
                        '& .MuiOutlinedInput-root': {
                          color: modalTextColor,
                          '& fieldset': {
                            borderColor: modalBorderColor,
                          },
                          '&:hover fieldset': {
                            borderColor: modalTextColor,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: accentColor,
                          },
                        },
                        '& .MuiInputBase-input': {
                          color: modalTextColor,
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: enableDarkTheme ? '#aaaaaa' : '#666666',
                          opacity: 1,
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        color: modalTextColor,
                        fontSize: '0.875rem',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: '14px',
                      }}
                    >
                      {row.sensorInput || ''}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {canEditSensor && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            padding: '16px 24px',
            borderTop: `1px solid ${modalBorderColor}`,
            backgroundColor: modalTitleBgColor,
          }}
        >
          <Button
            onClick={handleResetChanges}
            variant="outlined"
            color="error"
            disabled={!hasChanges}
            sx={{ minWidth: '100px' }}
          >
            {t('reset_changes')}
          </Button>
          <Button
            onClick={handleSaveClick}
            variant="contained"
            color="success"
            disabled={!canSaveChanges}
            sx={{ minWidth: '100px' }}
          >
            {t('save_all_changes')}
          </Button>
        </Box>
      )}

      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelSave}
        PaperProps={{
          sx: {
            backgroundColor: dialogBgColor,
            color: modalTextColor,
            border: `1px solid ${modalBorderColor}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: modalTitleBgColor,
            borderBottom: `1px solid ${modalBorderColor}`,
            padding: '12px 24px',
            fontSize: '1.25rem',
            fontWeight: 500,
            color: modalTextColor,
          }}
        >
          {t('confirm_save_changes')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: modalTextColor, marginBottom: 2, marginTop: 2 }}
          >
            {t('you_are_about_to_save_the_following_sensor_id_changes')}
          </DialogContentText>
          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {changes.map((change: any, index) => (
              <Box
                key={index}
                sx={{
                  padding: '8px 12px',
                  marginBottom: '8px',
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.05)',
                  borderRadius: '4px',
                  border: `1px solid ${modalBorderColor}`,
                }}
              >
                <Box
                  sx={{
                    fontWeight: 600,
                    color: modalTextColor,
                    fontSize: '0.875rem',
                  }}
                >
                  {change.tire} ({change.tireId})
                </Box>
                <Box
                  sx={{
                    color: modalTextColor,
                    fontSize: '0.8rem',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <span
                    style={{
                      color: enableDarkTheme ? '#ef4444' : '#dc2626',
                      textDecoration: 'line-through',
                    }}
                  >
                    {change.from}
                  </span>
                  <span>{t('x2192')}</span>
                  <span
                    style={{
                      color: enableDarkTheme ? '#22c55e' : '#16a34a',
                      fontWeight: 600,
                    }}
                  >
                    {change.to}
                  </span>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelSave}
            color="error"
            sx={{ color: modalTextColor }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmSave}
            color="success"
            variant="contained"
            autoFocus
          >
            {t('save_changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditableSensorTable;
